import { useCallback, useMemo } from 'react';
import { Flex } from '../../Core';
import { Checkbox } from '../Checkbox';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import { AutocompleteResult } from './AutocompleteResult';
import type { AutocompleteDropdownProps } from './types';

export type CheckboxAutocompleteDropdownProps<T> = Omit<
  AutocompleteDropdownProps<T>,
  'renderResult' | 'selectedItem'
> & { selectedItems: T[]; addSelectedItem: (item: T) => void; removeSelectedItem: (item: T) => void };

/** A dropdown implementation where each item has a checkbox to its left. */
export const CheckboxAutocompleteDropdown = <T,>({
  addSelectedItem,
  removeSelectedItem,
  selectedItems,
  ...props
}: CheckboxAutocompleteDropdownProps<T>) => {
  const selectedItemsSet = useMemo(() => new Set(selectedItems ?? []), [selectedItems]);

  const handleCheckedChange = useCallback(
    (item: T, newChecked: boolean) => {
      if (newChecked) {
        addSelectedItem(item);
      } else {
        removeSelectedItem(item);
      }
    },
    [addSelectedItem, removeSelectedItem]
  );
  const { isItemDisabled } = props;
  const prefix = (item: T) => (
    <Flex alignItems="center" mr="spacing4">
      <Checkbox
        checked={selectedItemsSet.has(item)}
        onChange={e => handleCheckedChange(item, e.target.checked)}
        disabled={isItemDisabled ? isItemDisabled(item) : false}
      />
    </Flex>
  );

  return (
    <AutocompleteDropdown
      {...props}
      renderResult={(searchResult, disabled, searchString) =>
        AutocompleteResult(searchResult, disabled, searchString, prefix)
      }
    />
  );
};
